import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { showToast } from '../components/ui/Toast';
import { knowledgeBase, findAnswer } from '../components/chatbot/knowledgeBase';

// Create context
const ChatContext = createContext();

// Default responses for specific cases not covered by knowledgeBase
const defaultResponses = {
  // ===== GENERAL GREETINGS =====
  "hello": "Hello! Welcome to UniTrade, your campus marketplace. I'm here to help you buy and sell safely within your university community. How can I assist you today?",
  "hi": "Hi there! I'm your UniTrade assistant. Whether you're looking for textbooks, selling items, or need help with your account, I'm here to help. What can I do for you today?",
  "hey": "Hey! Welcome to UniTrade support. I can help with buying, selling, account questions, or any other inquiries about our platform. What would you like to know?",
  "help": "I'm here to help! Here's what I can assist with:\n- Buying and selling processes\n- Account management\n- Shipping and returns\n- Payment questions\n- Safety and security\n- Campus-specific features\n\nWhat would you like to know more about?",
  "thanks": "You're welcome! If you have any more questions about UniTrade, whether now or in the future, don't hesitate to ask. We're here to make your campus buying and selling experience smooth and successful!",
  "thank you": "You're welcome! Remember, at UniTrade, we're committed to creating a thriving marketplace for university communities. Let me know if there's anything else I can help you with!",
  
  // ===== ACCOUNT MANAGEMENT =====
  "account": "Your UniTrade account is your passport to buying and selling within your campus community. With an account, you can:\n- List items for sale\n- Save favorite products\n- Message buyers/sellers\n- Track orders and sales\n- Manage your profile and preferences\n\nTo get started, click 'Register' on the top navigation or 'Login' if you already have an account.",
  
  "register": "Joining UniTrade is quick and free! Here's how to register:\n\n1. Click 'Register' in the top right\n2. Select account type: Buyer or Seller\n3. Enter your university email (@.edu)\n4. Create a strong password\n5. Complete your profile\n6. Verify your email\n\nStudent verification may be required for full access to all features.",
  
  "login": "To access your UniTrade account:\n1. Click 'Login' in the top right\n2. Enter your registered email\n3. Enter your password\n4. Complete 2FA if enabled\n\nHaving trouble? Try 'Forgot Password' or contact support.",
  
  "forgot password": "No worries! Reset your password in 3 easy steps:\n1. Go to Login page\n2. Click 'Forgot Password'\n3. Enter your email\n4. Check your inbox for reset instructions\n5. Create a new strong password\n\nDidn't receive the email? Check spam or contact support.",
  
  "verify email": "Email verification is required to access all UniTrade features. Here's what to do:\n1. Check your university email inbox\n2. Look for 'Verify Your UniTrade Account'\n3. Click the verification link\n4. Log in to your account\n\nCan't find the email? Check spam or request a new verification email from your account settings.",
  
  "delete account": "We're sorry to see you go! To delete your account:\n1. Go to Settings > Account\n2. Scroll to 'Delete Account'\n3. Read the information carefully\n4. Enter your password to confirm\n5. Click 'Permanently Delete'\n\n⚠️ Warning: This action is irreversible. All your data will be permanently removed.",
  
  "change password": "To update your password:\n1. Go to Settings > Security\n2. Select 'Change Password'\n3. Enter your current password\n4. Create a new strong password\n5. Confirm the new password\n6. Click 'Update Password'\n\nFor security, don't reuse passwords from other sites.",
  
  "profile": "Your profile helps build trust in the UniTrade community. You can update:\n- Profile picture\n- Display name\n- University and major\n- Contact information\n- Bio (optional)\n- Social media links (optional)\n\nTo edit: Go to Profile > Edit Profile\n\nTip: Complete profiles receive more responses from buyers/sellers!",
  
  // Buying Process
  "how to buy": "To buy a product: 1) Browse our listings, 2) Click on a product you're interested in, 3) Select any options and click 'Add to Cart', 4) Go to your Cart, and 5) Click 'Checkout' to complete your purchase.",
  "checkout": "During checkout, you'll be asked to confirm your shipping address, select a shipping method, and provide payment information. Once your payment is processed, your order will be placed.",
  "order tracking": "You can track your orders by going to your Account > Orders. Each order will display its current status and tracking information once shipped.",
  "cancel order": "You can cancel an order before it ships by going to Account > Orders > View Order > Cancel Order. Once an order has shipped, you'll need to follow our return process.",
  "buy from student": "Buying from student sellers works just like any other purchase on UniTrade. Student sellers are verified members of educational institutions who can offer special rates on textbooks and supplies.",
  
  // Selling Process
  "seller": "To become a seller, register for an account and select 'Seller' as your account type. You can then list products through your seller dashboard.",
  "sell": "To sell on UniTrade: 1) Create a seller account, 2) Go to your Seller Dashboard, 3) Click 'Add New Product', 4) Fill in the product details and upload images, 5) Set your price and inventory, 6) Publish your listing.",
  "how to sell": "To sell on UniTrade: 1) Create a seller account, 2) Go to your Seller Dashboard, 3) Click 'Add New Product', 4) Fill in the product details and upload images, 5) Set your price and inventory, 6) Publish your listing.",
  "commission": "UniTrade charges a 5% commission on sales for regular sellers and 3% for verified student sellers. Commission is calculated from the final sale price excluding shipping.",
  "student seller": "Student sellers need to provide valid student ID during registration. Once verified, you'll have access to reduced commission rates and the ability to list academic materials.",
  "professional seller": "Professional sellers go through a verification process. Once approved, you'll gain access to bulk listing tools, advanced analytics, and priority customer support.",
  
  // Shipping & Returns
  "shipping": "We typically ship orders within 1-2 business days. Delivery times vary based on your location, usually 3-5 business days within the same region.",
  "shipping cost": "Shipping costs are calculated based on the product weight, dimensions, and your delivery location. You can see the exact shipping cost during checkout before finalizing your purchase.",
  "food delivery time": "Food delivery time varies due to location, standard time is 30 minutes.",
  "delivery time": "Standard delivery typically takes 3-5 business days within the same country. International shipping can take 7-14 business days. Expedited shipping options are available at checkout for an additional fee.",
  "return policy": "We offer a 30-day return policy for most items. Products must be in original condition with all packaging. Contact customer support to initiate a return.",
  "refund": "Refunds are processed within 5-7 business days after we receive and inspect the returned item. The refund will be issued to the original payment method used for the purchase.",
  "exchange": "To exchange an item, please initiate a return through your account and make a new purchase for the desired item. This ensures accurate inventory management.",
  "damaged item": "If you receive a damaged item, please take photos of the damage and contact us within 48 hours of receiving your order. We'll arrange a replacement or refund.",
  
  // Payments
  "payment methods": "We accept credit/debit cards (Visa, Mastercard, American Express), PayPal, and mobile payment options like Apple Pay and Google Pay. All transactions are secure and encrypted.",
  "payment security": "Your payment information is fully encrypted and processed through secure payment gateways. UniTrade never stores your complete credit card information on our servers.",
  "coupon": "To use a coupon, enter the coupon code in the designated field during checkout. The discount will be automatically applied if the code is valid and applicable to your purchase.",
  "discount": "We regularly offer discounts through promotional campaigns. Subscribe to our newsletter to receive updates on upcoming sales and special offers.",
  "invoice": "You can download the invoice for your purchase from your Account > Orders > View Order > Download Invoice.",
  
  // Product Information
  "product quality": "All products on UniTrade are reviewed before listing. Sellers maintain ratings visible on their profiles to help you assess their reliability.",
  "used products": "Used products must include clear photos and accurate condition descriptions. We use a standard condition rating system: Like New, Very Good, Good, Acceptable, or For Parts.",
  "textbooks": "Textbooks sold by student sellers include information about the edition, any highlighting or notes, and whether access codes (if originally included) have been used.",
  "authenticity": "We take product authenticity seriously. All designer and brand-name items must be authentic. Counterfeit items are strictly prohibited and will result in seller account termination.",
  
  // Technical Issues
  "website issues": "If you're experiencing website issues, try clearing your browser cache, using a different browser, or accessing the site from a different device. If problems persist, please contact our support team.",
  "app not working": "If the mobile version is not working properly, make sure you're using the latest version of your web browser, clear your browser cache, or try using a different browser.",
  "payment failed": "If your payment failed, check that your card details are entered correctly and that your card hasn't expired. Also verify that your bank isn't blocking the transaction. You can try a different payment method or contact your bank for assistance.",
  "error": "If you're seeing an error message, please take a screenshot and send it to our support team at support@unitrade.com along with details about what you were trying to do when the error occurred.",
  
  // Contact & Support
  "contact": "You can reach our customer service team at support@unitrade.com, through this chat feature, or by calling our support line at 1-800-UNI-TRADE during business hours (9 AM - 6 PM EST, Monday-Friday).",
  "speak to human": "If you'd like to speak with a human representative, I can connect you with one of our support team members. Would you like me to do that for you?",
  "speak to agent": "I'd be happy to connect you with a human agent. Would you like me to transfer this conversation to our customer support team?",
  "talk to human": "I understand you'd like to talk to a human agent. I can transfer this conversation to our support team. Would you like me to do that now?",
  "human agent": "I can connect you with a human agent who can assist you further. Would you like me to transfer this chat to our customer support team?"
};

export const ChatProvider = ({ children }) => {
  // State management
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      text: "Hello! Welcome to UniTrade. How can I help you today?",
      sender: 'bot',
      timestamp: new Date().toISOString()
    }
  ]);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  
  // Refs
  const messagesEndRef = useRef(null);
  const wasOpen = useRef(false);

  // Process incoming messages and generate appropriate responses
  const processMessage = useCallback((message) => {
    // Check for contact form requests
    const lowerMessage = message.toLowerCase().trim();
    if (lowerMessage === 'yes' && messages[messages.length - 1]?.text.includes('contact an admin')) {
      setShowContactForm(true);
      return "I'll show you a contact form to fill out. Please provide your email and question, and our team will get back to you shortly.";
    }
    
    // Use the findAnswer function from knowledgeBase.js
    const response = findAnswer(message);
    
    // If no good match found, offer to connect with support
    if (response.includes("I'm not sure")) {
      return `${response} Would you like me to connect you with a human support agent?`;
    }
    
    return response;
  }, [messages]);

  const sendMessage = useCallback(async (message) => {
    if (!message.trim() || isSending) return;
    
    try {
      // Add user message to chat
      const userMessage = {
        id: 'msg_' + Date.now(),
        text: message,
        sender: 'user',
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, userMessage]);
      setIsSending(true);
      
      // Simulate typing delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Process message and get response
      const responseText = processMessage(message);
      
      // Add bot response to chat
      const botMessage = {
        id: 'msg_' + Date.now(),
        text: responseText,
        sender: 'bot',
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, botMessage]);
      
      // If the response indicates uncertainty, ask if they want to contact support
      if (responseText.includes("I'm not sure") || 
          responseText.includes("Could you rephrase")) {
        const followUpMessage = {
          id: 'msg_' + (Date.now() + 1),
          text: "Would you like to contact an admin for further assistance?",
          sender: 'bot',
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, followUpMessage]);
      }
      
    } catch (error) {
      console.error('Error sending message:', error);
      showToast('error', 'Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
      
      // Scroll to bottom after message is added
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [isSending, processMessage]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Add notification function (currently just shows a toast)
  const addNotification = useCallback((title, message) => {
    showToast('info', message);
  }, []);

  // Toggle chat window visibility
  const toggleChatWindow = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setIsOpen(prev => !prev);
    
    // If opening the chat, ensure it's not minimized
    if (!isOpen) {
      setIsMinimized(false);
    }
    
    // Mark that the chat has been opened at least once
    wasOpen.current = true;
  }, [isOpen]);

  return (
    <ChatContext.Provider
      value={{
        isOpen,
        isMinimized,
        messages,
        setMessages,
        isSending,
        showContactForm,
        setShowContactForm,
        toggleChatWindow,
        sendMessage,
        toggleMinimize: () => setIsMinimized(prev => !prev),
        messagesEndRef,
        openChat: () => {
          setIsOpen(true);
          setIsMinimized(false);
        },
        closeChat: () => {
          setIsOpen(false);
        }
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
